import { useNavigate } from "@tanstack/react-router";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { createPortal } from "react-dom";
import { ContextAttention } from "@/components/activity/ambient-attention";
import type { TimelineItem } from "@/components/activity/attention-inbox";
import { useOrgContext } from "@/hooks/use-org-context";
import { parseAppHref } from "@/lib/activity-links";
import { focusLobuHostTab } from "@/lib/extension-bridge";
import {
	LENS_CONTEXT_EVENT,
	LENS_CONTEXT_REQUEST_EVENT,
	type LobuLensContext,
	lensContextScopeKey,
	parseLensEventDetail,
} from "@/lib/lens-context";

const LENS_SIZE = { width: 340, height: 360 };
const DEBUG_LENS_SIZE = { width: 340, height: 436 };

interface DocumentPictureInPictureApi {
	requestWindow(options: { width: number; height: number }): Promise<Window>;
}

interface LobuLensValue {
	available: boolean;
	isOpen: boolean;
	open: () => Promise<void>;
}

const LobuLensStateContext = createContext<LobuLensValue | null>(null);

function documentPictureInPictureApi(): DocumentPictureInPictureApi | null {
	return (
		(
			window as Window & {
				documentPictureInPicture?: DocumentPictureInPictureApi;
			}
		).documentPictureInPicture ?? null
	);
}

function copyAppDocumentStyles(target: Document): void {
	const base = target.createElement("base");
	base.href = document.baseURI;
	target.head.append(base);
	for (const source of document.querySelectorAll<
		HTMLLinkElement | HTMLStyleElement
	>('link[rel="stylesheet"], style')) {
		const clone = source.cloneNode(true) as HTMLLinkElement | HTMLStyleElement;
		if (source instanceof HTMLLinkElement && clone instanceof HTMLLinkElement) {
			clone.href = source.href;
		}
		target.head.append(clone);
	}
	target.documentElement.className = document.documentElement.className;
	target.documentElement.style.colorScheme = getComputedStyle(
		document.documentElement,
	).colorScheme;
	target.body.style.margin = "0";
	target.body.style.width = "100vw";
	target.body.style.height = "100vh";
	target.body.style.overflow = "hidden";
}

export function LensSurface({
	owner,
	activeContext,
}: {
	owner: string;
	activeContext: LobuLensContext | null;
}) {
	const navigate = useNavigate();

	const openItem = useCallback(
		(item: TimelineItem) => {
			void (async () => {
				if (!item.href) return;
				const { pathname, search } = parseAppHref(item.href);
				await navigate({
					to: pathname as "/",
					search: (search ?? {}) as never,
				});
				await focusLobuHostTab();
			})();
		},
		[navigate],
	);
	const context: LobuLensContext = activeContext ?? {
		kind: "browser",
		url: null,
		title: null,
		applicationName: "Chrome",
	};
	return (
		<main
			className="lobu-lens-surface bg-background text-foreground"
			aria-label="Context notifications"
		>
			<div className="lobu-lens-content">
				<ContextAttention
					key={`${owner}:${lensContextScopeKey(context)}`}
					owner={owner}
					context={context}
					compact
					onOpenItem={openItem}
					emptyState={
						<div className="lobu-lens-empty text-muted-foreground">
							All caught up
						</div>
					}
				/>
			</div>
		</main>
	);
}

export function LobuLensProvider({ children }: { children: ReactNode }) {
	const { currentOwner } = useOrgContext();
	const owner = currentOwner ?? null;
	const [activeContext, setActiveContext] = useState<LobuLensContext | null>(
		() => ({
			kind: "browser",
			url: window.location.href,
			title: document.title || null,
			applicationName: "Browser",
		}),
	);
	const [portalNode, setPortalNode] = useState<HTMLElement | null>(null);
	const pipWindowRef = useRef<Window | null>(null);

	const open = useCallback(async () => {
		if (pipWindowRef.current && !pipWindowRef.current.closed) {
			pipWindowRef.current.focus();
			return;
		}
		if (!owner) throw new Error("Open a Lobu workspace before starting Lens.");
		const api = documentPictureInPictureApi();
		if (!api) {
			throw new Error(
				"This browser does not support Document Picture-in-Picture.",
			);
		}
		const debuggerAttached =
			document.documentElement.dataset.owlettoDebuggerAttached === "true";
		const pipWindow = await api.requestWindow(
			debuggerAttached ? DEBUG_LENS_SIZE : LENS_SIZE,
		);
		pipWindowRef.current = pipWindow;
		copyAppDocumentStyles(pipWindow.document);
		const mount = pipWindow.document.createElement("div");
		mount.id = "lobu-lens-root";
		mount.style.width = "100%";
		mount.style.height = "100%";
		pipWindow.document.body.append(mount);
		setPortalNode(mount);
		pipWindow.addEventListener(
			"pagehide",
			() => {
				if (pipWindowRef.current !== pipWindow) return;
				pipWindowRef.current = null;
				setPortalNode(null);
			},
			{ once: true },
		);
	}, [owner]);

	useEffect(() => {
		const onContext = (event: Event) => {
			const next = parseLensEventDetail<LobuLensContext>(event);
			if (next?.kind === "browser" || next?.kind === "application") {
				setActiveContext(next);
			}
		};
		document.addEventListener(LENS_CONTEXT_EVENT, onContext);
		document.dispatchEvent(new Event(LENS_CONTEXT_REQUEST_EVENT));
		return () => {
			document.removeEventListener(LENS_CONTEXT_EVENT, onContext);
			pipWindowRef.current?.close();
		};
	}, []);

	const value = useMemo<LobuLensValue>(
		() => ({
			available: owner != null && documentPictureInPictureApi() != null,
			isOpen: portalNode != null,
			open,
		}),
		[open, owner, portalNode],
	);

	return (
		<LobuLensStateContext.Provider value={value}>
			{children}
			{portalNode && owner
				? createPortal(
						<LensSurface owner={owner} activeContext={activeContext} />,
						portalNode,
					)
				: null}
		</LobuLensStateContext.Provider>
	);
}

export function useLobuLens(): LobuLensValue {
	const value = useContext(LobuLensStateContext);
	if (!value) {
		return {
			available: false,
			isOpen: false,
			open: async () => {},
		};
	}
	return value;
}
